import React, { useEffect } from 'react';

import {
  PaymentStatus,
} from '@alpha/payments-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Box } from '@alpha/ui-lib/ui/external';
import { APMainLayout } from '@alpha/ui-lib/ui/MainLayout';
import { StyledTabsWrapper, TabContent } from '@alpha/ui-lib/ui/Tabs';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FullPageLoader from '../../../components/FullPageLoader/FullPageLoader';
import { CurrencyPair } from '../../../components/International';
import { DownloadType, FileType } from '../../../hooks/useReportsPolling';
import { flowToBatchStatusMapping } from '../../../utils/batchStatus.helpers';

import BatchDetails from './BatchDetails/BatchDetails';
import FooterContainer from './FooterContainer/FooterContainer';
import PaymentSummaryContainer from './PaymentSummary/PaymentSummary';
import TitleContainer from './TitleContainer/TitleContainer';
import { IBatchCurrencyPair } from './BatchSummary.interface';
import { useStyles } from './BatchSummary.styles';
import useBatchSummary from './useBatchSummary';

export const componentTestIds = {
  BatchSummary: 'batch-summary-screen',
  BatchSummaryTabs: 'batch-summary-tabs',
};

export const BatchSummary: React.FC = () => {
  const classes = useStyles();
  const {
    batchId,
    batchDetails,
    authyStatus,
    selectedTabIndex,
    routeParameterStats,
    getBreadcrumbs,
    handleSuccessfulAuthy,
    updateBatchDetails,
    handleReportGeneration,
    handleSetSelectedTabIndex,
  } = useBatchSummary();

  useEffect(() => {
    if (authyStatus === 'SUCCESS') {
      handleSuccessfulAuthy(authyStatus, batchId, routeParameterStats);
    }
  }, [batchId, authyStatus, routeParameterStats]);

  useEffect(() => {
    updateBatchDetails(batchId, routeParameterStats);
  }, []);

  if (!batchDetails) {
    return <FullPageLoader testId="batchsummary-fullpageloader" />;
  }

  const getCurrencyPairTabs = (): any[] => {
    if (batchDetails) {
      return ([batchDetails.debitingCurrency, batchDetails.fundingCurrency] as any).map(
        (currencyPair: IBatchCurrencyPair) => {
          const { sellCurrency, buyCurrency } = currencyPair;
          if (sellCurrency === buyCurrency) {
            return {
              ...currencyPair,
              display: <CurrencyPair currencyCode={sellCurrency} />,
            };
          }
          return {
            ...currencyPair,
            display: `${sellCurrency}/${buyCurrency}`,
          };
        },
      );
    }

    return [];
  };
  const currencyPairTabs: Array<any> = getCurrencyPairTabs();

  return (
    <APMainLayout
      data-testid={componentTestIds.BatchSummary}
      title={<TitleContainer paymentDetails={batchDetails} isCrossCurrency />}
      breadCrumbs={getBreadcrumbs(batchDetails && batchDetails.status)}
      pageTabs={(
        <StyledTabsWrapper
          testId={componentTestIds.BatchSummaryTabs}
          tabTitles={currencyPairTabs.map((tab) => tab.display)}
          tabIndex={selectedTabIndex}
          handleChange={handleSetSelectedTabIndex}
        />
      )}
      rightAlignedCta={
        !flowToBatchStatusMapping.uploader.includes(
          batchDetails && batchDetails.status,
        )
          ? (
            <ActionButton
              style={{ background: '#F7F7F7', color: '#212529' }}
              className={classes.exportButton}
              size="medium"
              onClick={() => handleReportGeneration(FileType.PDF, DownloadType.Batch)}
            >
              <FontAwesomeIcon icon={faDownload as IconProp} />
              Export payment summary
            </ActionButton>
          ) : undefined
      }
    >
      {currencyPairTabs.map((currencyPairTab, i) => (
        <TabContent
          key={`${currencyPairTab.sellCurrency}/${currencyPairTab.buyCurrency}`}
          index={i}
          value={selectedTabIndex}
        >
          <Box className={classes.tabContainer}>
            {batchDetails && (
              <BatchDetails batchDetails={batchDetails} currencyPairTab={currencyPairTab} />
            )}
            {batchId // put batchId here
              && currencyPairTab.sellCurrency
              && currencyPairTab.buyCurrency
              && currencyPairTab.fixedSide
              && currencyPairTab.fixedSide
              && (
                <PaymentSummaryContainer
                  postApproval={Boolean(
                    batchDetails.status === PaymentStatus.COMPLETED
                    || batchDetails.status === PaymentStatus.PROCESSING,
                  )}
                  currencyPairId={currencyPairTab.id}
                  sellCurrencyCode={currencyPairTab.sellCurrency}
                  buyCurrencyCode={currencyPairTab.buyCurrency}
                  fixedSide={currencyPairTab.fixedSide}
                  batchId={batchId}
                />
              )}
          </Box>
        </TabContent>
      ))}
      <FooterContainer batchDetails={batchDetails} />
    </APMainLayout>
  );
};
